/**
 * Shared Romanian labels for price-list snapshot / history field paths.
 * Used by Compară liste and Istoric.
 */

export const PRICE_LIST_FIELD_LABELS = {
    product_code: "Cod produs",
    product_name: "Denumire comercială",
    measurement_unit: "U.M.",
    price: "Preț",
    description: "Descriere",
    visibleInOfferApp: "Vizibil program ofertare",
    status: "Status",
    list_price: "Preț listă",
    item: "Produs",
    pricing_detail: "Detalii produs",

    category_assignments: "Sisteme (categorii)",
    main_category: "Sistem",
    sub_category: "Subsistem",

    system1: "Sistem 1",
    system2: "Sistem 2",
    system3: "Sistem 3",
    systems: "Sisteme",
    systemSubcategories: "Subsisteme",
    chapterCode: "Cod categorie",
    chapterName: "Categorie",
    subchapterCode: "Cod subcategorie",
    subchapterName: "Subcategorie",
    progressiveNumber: "Număr progresiv",
    manufacturerName: "Producător",
    manufacturerCode: "Cod producător",
    serialNumber: "Serial nr.",
    manufacturerProductCode: "Cod produs furnizor",
    manufacturerProductName: "Denumire furnizor",
    weight: "Greutate",
    characteristics: "Note produs",
    nomenclatureCode: "Cod nomenclator",
    productNotes: "Note produs",
    isCompositeProduct: "Produs compus",
    commercialPolicyDiscount: "Discount politică comercială",
    overrideProductCode: "Suprascriere cod produs",
    overrideProductCodeValue: "Cod produs (suprascris)",

    furnizorPricings: "Detalii furnizor",
    furnizorCode: "Cod furnizor",
    furnizorName: "Furnizor",
    usesManufacturerAsFurnizor: "Producător ca furnizor",
    isDefault: "Furnizor implicit",
    clientKey: "Cheie client furnizor",
    supplierListPriceOld: "Preț listă furnizor (vechi)",
    supplierListPriceNew: "Preț listă furnizor",
    currency: "Monedă",
    paymentTermDays: "Termen plată (zile)",
    discount1: "Discount 1 (%)",
    discount2: "Discount 2 (%)",
    cashDiscount: "Cash discount (%)",
    marketingDiscount: "Marketing (%)",
    costUplifts: "Majorare costuri",
    costUpliftRonTotal: "Majorare costuri RON",
    stampDutyAmount: "Taxă de timbru",
    stampDutyCurrency: "Monedă taxă de timbru",
    stampDutyAmountRon: "Taxă de timbru (în RON)",
    stampDutyBreakdown: "Taxă de timbru (formulă)",
    transportCostEstimated: "Cost transport + alte taxe estimat",
    transportCostRate: "Cost transport + alte taxe (%)",
    markupReference: "Adaos referință (%)",
    markupActual: "Adaos actual (%)",
    notes: "Note",
    listPriceRonIncVatReference: "Preț listă RON cu TVA referință",
    priceAdjustmentRatio: "Majorare",
    overrideListPriceRonIncVatFinal: "Suprascriere preț listă RON cu TVA",
    overrideListPriceRonIncVatFinalValue: "Preț listă RON cu TVA (suprascris)",
    listPriceRonIncVatFinal: "Preț listă RON cu TVA actual",
    pricingSettingsSnapshot: "Snapshot setări curs",
    netEntryPriceExVat: "Preț intrare net fără TVA",
    finalEntryPriceExVat: "Preț intrare final fără TVA",
    entryPriceRonExVat: "Preț intrare RON fără TVA",
    entryPriceRonIncVat: "Preț intrare RON cu TVA",
    listPriceRonExVat: "Preț listă RON fără TVA",
    listPriceEuroExVat: "Preț listă EUR fără TVA",

    exchangeRateEur: "Curs EUR",
    exchangeRatePln: "Curs PLN",
    exchangeRateUsd: "Curs USD",
    vatRate: "TVA",
    pricing_settings: "Setări curs valutar",
};

function labelForKey(key) {
    if (!key) return "";
    if (PRICE_LIST_FIELD_LABELS[key]) return PRICE_LIST_FIELD_LABELS[key];
    return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Human-readable Romanian label for a snapshot/diff field path.
 * Examples:
 *  - list_price → Preț listă
 *  - item.product_name → Denumire comercială
 *  - pricing_detail.furnizorPricings.code:ABC.discount1 → Furnizor ABC · Discount 1 (%)
 */
export function humanizePriceListFieldPath(path) {
    if (!path) return "";
    if (PRICE_LIST_FIELD_LABELS[path]) return PRICE_LIST_FIELD_LABELS[path];

    const segments = [];
    const regex = /([^[.\]]+)|\[(\d+)\]/g;
    let match;
    while ((match = regex.exec(path)) !== null) {
        if (match[1] !== undefined) {
            segments.push({ type: "key", value: match[1] });
        } else {
            segments.push({ type: "index", value: Number(match[2]) });
        }
    }

    if (!segments.length) return path;

    const parts = [];
    let i = 0;

    if (segments[0].type === "key" && segments[0].value === "pricing_detail") {
        if (segments.length === 1) return "Detalii produs";
        i = 1;
    } else if (segments[0].type === "key" && segments[0].value === "item") {
        if (segments.length === 1) return "Produs";
        i = 1;
    } else if (segments[0].type === "key" && segments[0].value === "pricing_settings") {
        if (segments.length === 1) return "Setări curs";
        i = 1;
        parts.push("Setări curs");
    } else if (
        segments[0].type === "key" &&
        PRICE_LIST_FIELD_LABELS[segments[0].value] &&
        segments.length === 1
    ) {
        return PRICE_LIST_FIELD_LABELS[segments[0].value];
    }

    while (i < segments.length) {
        const seg = segments[i];

        if (seg.type === "key" && seg.value === "furnizorPricings") {
            const next = segments[i + 1];
            let furnizorLabel = "Furnizor";
            if (next && next.type === "key") {
                const raw = next.value;
                if (raw.startsWith("code:")) {
                    furnizorLabel = `Furnizor ${raw.slice(5)}`;
                } else if (raw.startsWith("client:")) {
                    furnizorLabel = `Furnizor (${raw.slice(7)})`;
                } else if (raw.startsWith("index:")) {
                    furnizorLabel = `Furnizor #${Number(raw.slice(6)) + 1}`;
                } else {
                    furnizorLabel = `Furnizor ${raw}`;
                }
                i += 2;
            } else if (next && next.type === "index") {
                furnizorLabel = `Furnizor #${next.value + 1}`;
                i += 2;
            } else {
                i += 1;
            }
            parts.push(furnizorLabel);
            continue;
        }

        if (seg.type === "key" && seg.value === "category_assignments") {
            parts.push("Sisteme");
            i += 1;
            if (segments[i] && segments[i].type === "index") {
                parts[parts.length - 1] = `Sistem #${segments[i].value + 1}`;
                i += 1;
            }
            continue;
        }

        if (seg.type === "index") {
            parts.push(`#${seg.value + 1}`);
            i += 1;
            continue;
        }

        parts.push(labelForKey(seg.value));
        i += 1;
    }

    return parts.filter(Boolean).join(" · ") || labelForKey(path);
}

/** Map booleans (and common string forms) to Da / Nu. Returns null if not a boolean. */
export function formatBooleanDisplay(value) {
    if (value === true || value === 1) return "Da";
    if (value === false || value === 0) return "Nu";
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true" || normalized === "1") return "Da";
        if (normalized === "false" || normalized === "0") return "Nu";
    }
    return null;
}

/**
 * Format a price-list history / compare cell value for display.
 * Booleans → Da / Nu; empty → —.
 */
export function formatPriceListFieldValue(value) {
    if (value === null || value === undefined || value === "") {
        return "—";
    }

    const boolLabel = formatBooleanDisplay(value);
    if (boolLabel !== null) return boolLabel;

    if (typeof value === "string" && (value.startsWith("{") || value.startsWith("["))) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed) && parsed.length === 0) return "—";
            if (
                parsed &&
                typeof parsed === "object" &&
                !Array.isArray(parsed) &&
                !Object.keys(parsed).length
            ) {
                return "—";
            }
            if (Array.isArray(parsed)) {
                return parsed
                    .map((row) => {
                        if (row && typeof row === "object") {
                            return (
                                [row.main_category, row.sub_category]
                                    .filter(Boolean)
                                    .join(" / ") || JSON.stringify(row)
                            );
                        }
                        const rowLabel = formatBooleanDisplay(row);
                        return rowLabel !== null ? rowLabel : String(row);
                    })
                    .join(", ");
            }
            return JSON.stringify(parsed);
        } catch {
            return value;
        }
    }

    return String(value);
}
